import { useCallback, useEffect, useState } from 'react';
import { Gavel, Info, Loader2, Sparkles } from 'lucide-react';
import { apiClient } from '../../../api';

const REASON_CODES = [
  'Core', 'Desktop', 'SaaS', 'Hardware', 'CM', 'OS Component', 'Sub Component',
  'Retired', 'Duplicated Function', 'Test', 'Other', 'Physical DC Tool',
];

interface Card {
  header: { eai_nbr: number; name: string; primary_data_center: string; install_type: string; classification: string; criticality: string; app_type: string | null };
  description: { short_description: string | null; app_dsc: string | null };
  rule_says: { fired: { rule_key: string; title: string; tier: string; false_exclusion_rate: string | null; rate_text: string; provisional: boolean; evidence: string }[]; evaluated: string[]; summary: string | null };
  model_says: {
    probability: number | null;
    terms_toward_exclude: string[];
    terms_toward_migrate: string[];
    language: { decision: string; reason_code: string | null; citation: string | null; rationale: string | null } | null;
  };
  precedent: { eai_nbr: number; name: string; outcome: string; similarity: number }[];
}

interface QueueItem { position: number; eai_nbr: number; name: string; band: number; probability: number | null; card: Card }

interface Promotion {
  decision: string;
  field: string;
  value: string;
  call_count: number;
  measured: { precision: number | null; rate_text: string; coverage_unlabelled: number; labelled_fires: number };
  suggested_rule: { rule_key: string; title: string; tier: string; predicate: unknown; reason_code: string | null };
}

export default function DispositionWorkbench({ refreshKey, scope, scopeLoaded, basis }: {
  refreshKey: number;
  scope: string | null;
  scopeLoaded: boolean;
  basis: string;
}) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingEai, setSavingEai] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [proposed, setProposed] = useState<Record<string, boolean>>({});

  const loadPromotions = useCallback(() => {
    apiClient.get('/api/cloud-portfolio/workbench-promotions')
      .then((r) => setPromotions(r.data?.suggestions || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!scopeLoaded) return undefined;
    if (!scope) { setQueue([]); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setIndex(0);
    apiClient.get('/api/cloud-portfolio/workbench', { params: { scope, basis } })
      .then((q) => {
        if (cancelled) return;
        setQueue(q.data?.items || []);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e.message || 'Failed to load the queue');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    loadPromotions();
    return () => { cancelled = true; };
  }, [scope, scopeLoaded, basis, refreshKey, loadPromotions]);

  const item = queue[index] || null;

  const rule = (decision: 'MIGRATES' | 'DOES_NOT_MIGRATE' | 'UNDECIDED') => {
    if (!item) return;
    const eai = item.eai_nbr;
    setSavingEai(eai);
    apiClient.post('/api/cloud-portfolio/decisions', {
      eai_nbr: eai,
      decision,
      reason_code: decision === 'DOES_NOT_MIGRATE' ? reason || null : null,
      note: note || null,
    })
      .then(() => { setError(null); loadPromotions(); })
      .catch((e) => setError(`EAI ${eai}: ${e?.response?.data?.error || e.message || 'decision failed'} — it stays in the queue on refresh`))
      .finally(() => setSavingEai((s) => (s === eai ? null : s)));
    setIndex((i) => i + 1);
    setReason('');
    setNote('');
  };

  const propose = async (p: Promotion) => {
    try {
      await apiClient.post('/api/cloud-portfolio/rules', p.suggested_rule);
      setProposed((m) => ({ ...m, [p.suggested_rule.rule_key]: true }));
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to propose the rule');
    }
  };

  if (!scopeLoaded || loading) return <p className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {scope || ''} queue (one fetch — everything after this is instant)…</p>;

  if (!scope) {
    return (
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        Pick a datacentre on the Scope tab first — the workbench works one scoped queue at a time.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {promotions.map((p) => (
        <div key={p.suggested_rule.rule_key} className="flex items-center justify-between gap-3 text-xs bg-violet-50 border border-violet-200 rounded-lg px-3 py-2.5">
          <span className="text-violet-900">
            <Sparkles className="w-3.5 h-3.5 inline mr-1" />
            You made the same call ({p.decision.replace(/_/g, ' ').toLowerCase()}) {p.call_count}× on apps sharing {p.field} = “{p.value}”.
            {' '}Measured precision {p.measured?.precision != null ? Math.round(p.measured.precision * 100) + '%' : '—'} on {p.measured?.labelled_fires ?? 0} labelled · would fire on {p.measured?.coverage_unlabelled ?? 0} unlabelled apps.
          </span>
          {proposed[p.suggested_rule.rule_key] ? (
            <span className="text-violet-700 font-semibold whitespace-nowrap">Proposed ✓ — approve it on the rule library</span>
          ) : (
            <button onClick={() => propose(p)} className="px-2.5 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white font-semibold whitespace-nowrap">
              Promote to rule
            </button>
          )}
        </div>
      ))}

      {!item && (
        <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
          <Gavel className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {queue.length
            ? `Queue for ${scope} is done — all ${queue.length} applications ruled this session.`
            : `Queue for ${scope} is empty — every live application in this scope has a decision.`}
        </div>
      )}

      {item && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div>
              <p className="text-sm text-super-dark-purple">EAI {item.card.header.eai_nbr} · <span className="font-bold">{item.card.header.name}</span></p>
              <p className="text-xs text-gray-500 mt-0.5">
                {[item.card.header.primary_data_center, item.card.header.install_type, item.card.header.classification, item.card.header.criticality, item.card.header.app_type]
                  .filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {savingEai != null && <span className="text-[11px] text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> saving…</span>}
              <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: '#F5F3FF', color: '#221551' }}>
                {index + 1} of {queue.length} in queue
              </span>
            </div>
          </div>

          {(item.card.description.short_description || item.card.description.app_dsc) && (
            <div className="text-xs text-gray-700 border-l-2 pl-3 space-y-1" style={{ borderColor: '#E5E7EB' }}>
              {item.card.description.short_description && <p>{item.card.description.short_description}</p>}
              {item.card.description.app_dsc && item.card.description.app_dsc !== item.card.description.short_description && <p>{item.card.description.app_dsc}</p>}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="p-3 rounded" style={{ background: '#F1EFE8' }}>
              <p className="font-bold mb-1" style={{ color: '#2C2C2A' }}>Rule says</p>
              {item.card.rule_says.fired.length === 0 ? (
                <>
                  <p style={{ color: '#5F5E5A' }}>No gate fired.</p>
                  <p className="mt-1" style={{ color: '#8A897F' }}>Evaluated: {item.card.rule_says.evaluated.join(', ')}</p>
                </>
              ) : item.card.rule_says.fired.map((g) => (
                <div key={g.rule_key} className="mb-1.5">
                  <p style={{ color: '#5F5E5A' }}>
                    <span className="font-semibold">{g.title}</span> ({g.tier})
                    {g.provisional && <span className="ml-1 text-[10px] font-bold px-1 py-0.5 rounded" style={{ background: '#FDE68A', color: '#78350F' }}>PROVISIONAL</span>}
                  </p>
                  <p style={{ color: '#8A897F' }}>error {g.rate_text} · {g.evidence}</p>
                </div>
              ))}
            </div>

            <div className="p-3 rounded" style={{ background: '#EEEDFE' }}>
              <p className="font-bold mb-1" style={{ color: '#26215C' }}>
                Model says{item.card.model_says.probability != null ? ` · ${item.card.model_says.probability}` : ''}
              </p>
              {item.card.model_says.probability == null ? (
                <p style={{ color: '#534AB7' }}>No scorer trained yet.</p>
              ) : (
                <p style={{ color: '#534AB7' }}>
                  {item.card.model_says.probability >= 0.7 ? 'Likely migrates' : item.card.model_says.probability <= 0.25 ? 'Likely does not migrate' : 'Uncertain — interview territory'}
                </p>
              )}
              {item.card.model_says.terms_toward_exclude.length > 0 && (
                <p className="mt-1" style={{ color: '#534AB7' }}>Toward exclude: <em>{item.card.model_says.terms_toward_exclude.join(', ')}</em></p>
              )}
              {item.card.model_says.terms_toward_migrate.length > 0 && (
                <p className="mt-0.5" style={{ color: '#534AB7' }}>Toward migrate: <em>{item.card.model_says.terms_toward_migrate.join(', ')}</em></p>
              )}
              {item.card.model_says.language && (
                <p className="mt-1.5 pt-1.5 border-t" style={{ color: '#534AB7', borderColor: '#DDD9FB' }}>
                  Language layer: {item.card.model_says.language.decision.replace(/_/g, ' ')}
                  {item.card.model_says.language.reason_code ? ` (${item.card.model_says.language.reason_code})` : ''}
                  {item.card.model_says.language.citation ? <> — “{item.card.model_says.language.citation}”</> : null}
                </p>
              )}
            </div>

            <div className="p-3 rounded" style={{ background: '#E1F5EE' }}>
              <p className="font-bold mb-1" style={{ color: '#04342C' }}>Precedent</p>
              {item.card.precedent.length === 0 ? (
                <p style={{ color: '#0F6E56' }}>No similar dispositioned application found.</p>
              ) : item.card.precedent.map((p) => (
                <p key={p.eai_nbr} style={{ color: '#0F6E56' }}>{p.name} → <span className="font-semibold">{p.outcome}</span></p>
              ))}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap items-center pt-1">
            <button onClick={() => rule('MIGRATES')} className="text-xs px-3 py-1.5 rounded text-white font-semibold" style={{ background: '#221551' }}>
              Migrates
            </button>
            <button onClick={() => rule('DOES_NOT_MIGRATE')} className="text-xs px-3 py-1.5 rounded border border-gray-300 font-semibold text-gray-700 hover:bg-gray-50">
              Does not migrate
            </button>
            <button onClick={() => rule('UNDECIDED')} className="text-xs px-3 py-1.5 rounded border border-gray-300 font-semibold text-gray-700 hover:bg-gray-50">
              Needs interview
            </button>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1.5">
              <option value="">Reason…</option>
              {REASON_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional)"
              className="text-xs border border-gray-300 rounded px-2 py-1.5 flex-1 min-w-[160px]"
            />
          </div>
          <p className="text-[11px] text-gray-400">
            Rule and model state their case above; your call is recorded as HUMAN provenance either way, appended to the decision trail.
          </p>
        </div>
      )}
    </div>
  );
}
